import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import DecisionModal from "../components/panels/DecisionModal";
import DocumentPreviewModal from "../components/panels/DocumentPreviewModal";
import DocumentsList from "../components/panels/DocumentsList";
import DocumentUploadModal from "../components/panels/DocumentUploadModal";
import FinalReportModal from "../components/panels/FinalReportModal";
import FinalReportSection from "../components/panels/FinalReportSection";
import PanelActions from "../components/panels/PanelActions";
import PanelHeader from "../components/panels/PanelHeader";
import PanelistsList from "../components/panels/PanelistsList";
import PanelStatistics from "../components/panels/PanelStatistics";
import StatusTimeline from "../components/panels/StatusTimeline";
import AlertBanner from "../components/shared/AlertBanner";
import { useAuth } from "../hooks/useAuth";
import { useDocumentUpload } from "../hooks/useDocumentUpload";
import { useFinalReport } from "../hooks/useFinalReport";
import { usePanelData } from "../hooks/usePanelData";
import { usePanelDecision } from "../hooks/usePanelDecision";

const PanelAdminInterface = () => {
  const { user, isPanelAdmin, hasRole } = useAuth();
  const { panelId } = useParams();
  const navigate = useNavigate();

  // Custom hooks for data and logic
  const {
    panel,
    panelists,
    documents,
    progress,
    status,
    finalReport,
    loading,
    fetchPanelData,
    updateProgress,
    updateStatus,
    updateDocuments,
    updateFinalReport,
    setProgress,
  } = usePanelData(panelId);

  // Modal states
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [showDecisionModal, setShowDecisionModal] = useState(false);
  const [showDocumentPreview, setShowDocumentPreview] = useState(null);

  // Form states
  const [newDocument, setNewDocument] = useState({
    name: "",
    type: "PDF",
    description: "",
    file: null,
  });
  const [decisionNotes, setDecisionNotes] = useState("");
  const [notification, setNotification] = useState(null);

  // Custom hooks for actions
  const { uploadDocument, isUploading, uploadProgress } = useDocumentUpload(
    panelId,
    user,
    (doc) => {
      updateDocuments([...documents, doc]);
      setNewDocument({ name: "", type: "PDF", description: "", file: null });
      setShowUploadModal(false);
      updateProgress(10);
    }
  );

  const { createDecision } = usePanelDecision(panelId, user, async () => {
    updateStatus("Active");
    setProgress(90);
    setShowDecisionModal(false);
    setDecisionNotes("");
    await fetchPanelData();
  });

  const { uploadFinalReport } = useFinalReport(
    panelId,
    panel?.title,
    user,
    async (report) => {
      updateStatus("Active");
      setProgress(95);
      updateFinalReport(report);
      setShowReportModal(false);
      await fetchPanelData();
    }
  );

  // Handlers
  const handleDocumentUpload = async () => {
    await uploadDocument(newDocument);
  };

  const handleDocumentDelete = (docId) => {
    const updatedDocuments = documents.filter((d) => d.id !== docId);
    updateDocuments(updatedDocuments);

    // Save to localStorage for backward compatibility
    const savedPanels = JSON.parse(localStorage.getItem("panels") || "[]");
    const updatedPanels = savedPanels.map((p) =>
      p.id === parseInt(panelId) ? { ...p, documents: updatedDocuments } : p
    );
    localStorage.setItem("panels", JSON.stringify(updatedPanels));

    showNotificationMessage("Document removed successfully");
  };

  const handleMarkDecisionComplete = () => {
    setShowDecisionModal(true);
  };

  const confirmDecisionComplete = async () => {
    await createDecision(decisionNotes);
  };

  const handleShareFinalReport = () => {
    setShowReportModal(true);
  };

  const handleUploadFinalReport = async (data) => {
    await uploadFinalReport(data);
  };

  const showNotificationMessage = (message, type = "success") => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 5000);
  };

  // Check if user has access to manage this panel
  const hasAccess =
    hasRole("PLATFORM_ADMIN") || isPanelAdmin(parseInt(panelId));

  if (!hasAccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Access Denied
          </h2>
          <p className="text-gray-600 mb-4">
            You are not authorized to manage this panel.
          </p>
          <button
            onClick={() => navigate("/dashboard")}
            className="bg-custom-blue text-white px-6 py-2 rounded-lg hover:bg-custom-light-blue transition-colors"
          >
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-custom-blue mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading panel data...</p>
        </div>
      </div>
    );
  }

  if (!panel) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-xl text-gray-600">Panel not found</p>
          <button
            onClick={() => navigate("/dashboard")}
            className="mt-4 px-4 py-2 bg-custom-blue text-white rounded-lg hover:bg-custom-light-blue"
          >
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <PanelHeader panel={panel} status={status} progress={progress} />

      {/* Notification */}
      {notification && (
        <AlertBanner
          type={notification.type}
          message={notification.message}
          onClose={() => setNotification(null)}
          className="mb-6 animate-fadeIn"
        />
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Panelists */}
          <PanelistsList panelists={panelists} />

          {/* Documents */}
          <DocumentsList
            documents={documents}
            onUpload={() => setShowUploadModal(true)}
            onPreview={(doc) => setShowDocumentPreview(doc)}
            onDelete={handleDocumentDelete}
          />

          {/* Final Report Section */}
          <FinalReportSection
            status={status}
            finalReport={finalReport}
            onUpload={handleShareFinalReport}
          />
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Status Timeline */}
          <StatusTimeline status={status} />

          {/* Actions */}
          <PanelActions
            status={status}
            finalReport={finalReport}
            onMarkDecisionComplete={handleMarkDecisionComplete}
            onUploadReport={handleShareFinalReport}
            panelId={panelId}
          />

          {/* Quick Stats */}
          <PanelStatistics
            panel={panel}
            documents={documents}
            panelists={panelists}
          />
        </div>
      </div>

      {/* Modals */}
      <DocumentUploadModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        newDocument={newDocument}
        setNewDocument={setNewDocument}
        onUpload={handleDocumentUpload}
        isUploading={isUploading}
        uploadProgress={uploadProgress}
      />

      <FinalReportModal
        isOpen={showReportModal}
        onClose={() => setShowReportModal(false)}
        onSubmit={handleUploadFinalReport}
      />

      <DecisionModal
        isOpen={showDecisionModal}
        onClose={() => setShowDecisionModal(false)}
        decisionNotes={decisionNotes}
        setDecisionNotes={setDecisionNotes}
        onConfirm={confirmDecisionComplete}
      />

      <DocumentPreviewModal
        isOpen={!!showDocumentPreview}
        onClose={() => setShowDocumentPreview(null)}
        document={showDocumentPreview}
      />
    </div>
  );
};

export default PanelAdminInterface;
